import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = () => {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    locations: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please complete name, phone and email.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        locations: form.locations
          .split(/[,;\n]/)
          .map((s) => s.trim())
          .filter(Boolean),
        message: form.message.trim() || undefined,
      };
      await axios.post(`${API}/leads`, payload);
      setSubmitted(true);
      toast.success("Confirmation sent. Check your email for further instructions.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/brochure`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "AIO_130_Destinations_Brochure.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not download brochure. Please try again in a moment.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section
      id="reserve"
      data-testid="reserve-section"
      className="relative py-24 sm:py-32 bg-aio-bg"
    >
      <div className="grain" />
      <div className="relative max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-20 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <p className="text-[11px] uppercase tracking-[0.32em] text-aio-soft mb-6">
            Reserve your consultation
          </p>
          <h2 className="font-display text-5xl sm:text-6xl leading-[1.02] tracking-tighter text-aio-ink mb-8">
            Choose your <span className="italic">luxury</span> stay.
          </h2>
          <p className="text-aio-soft leading-relaxed text-base sm:text-lg max-w-md mb-10">
            Submit your full name, phone number, preferred locations and email.
            You&apos;ll receive a confirmation email with further instructions
            from your dedicated A.I.O sourcing specialist.
          </p>

          <dl className="space-y-4 text-sm">
            {[
              ["01", "Submit your details below."],
              ["02", "Receive a confirmation email within 24 hours."],
              ["03", "A specialist curates stays for your locations."],
              ["04", "Confirm & travel — everything sourced end-to-end."],
            ].map(([n, t]) => (
              <div key={n} className="flex gap-6 items-baseline">
                <dt className="font-display italic text-2xl text-aio-olive w-8">{n}</dt>
                <dd className="text-aio-ink font-light">{t}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="lg:col-span-7">
          <div className="relative bg-aio-surface p-8 sm:p-12 border border-aio-border/60">
            <AnimatePresence mode="wait">
              {!submitted ? (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  data-testid="lead-form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-8"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.28em] text-aio-soft">
                        Full name
                      </span>
                      <input
                        data-testid="input-full-name"
                        type="text"
                        required
                        value={form.full_name}
                        onChange={update("full_name")}
                        className="editorial-input"
                        placeholder="Ada Lovelace"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.28em] text-aio-soft">
                        Phone number
                      </span>
                      <input
                        data-testid="input-phone"
                        type="tel"
                        required
                        value={form.phone}
                        onChange={update("phone")}
                        className="editorial-input"
                        placeholder="+1 555 123 4567"
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.28em] text-aio-soft">
                      Email address
                    </span>
                    <input
                      data-testid="input-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      className="editorial-input"
                      placeholder="ada@studio.com"
                    />
                  </label>

                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.28em] text-aio-soft">
                      Preferred locations
                    </span>
                    <input
                      data-testid="input-locations"
                      type="text"
                      value={form.locations}
                      onChange={update("locations")}
                      className="editorial-input"
                      placeholder="Maldives, Santorini, Cabo San Lucas"
                    />
                    <span className="mt-2 block text-[11px] text-aio-soft/80">
                      Separate multiple locations with commas.
                    </span>
                  </label>

                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.28em] text-aio-soft">
                      Anything else? <span className="normal-case tracking-normal text-aio-soft/70">(optional)</span>
                    </span>
                    <textarea
                      data-testid="input-message"
                      rows={2}
                      value={form.message}
                      onChange={update("message")}
                      className="editorial-input resize-none"
                      placeholder="Travel dates, group size, occasion…"
                    />
                  </label>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 pt-4">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="brochure-submit-button"
                      className="inline-flex items-center gap-3 px-8 py-4 bg-aio-olive text-aio-bg hover:bg-aio-oliveDeep transition-colors text-sm uppercase tracking-[0.22em] disabled:opacity-50"
                    >
                      {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                      {submitting ? "Submitting" : "Request Brochure"}
                      {!submitting && <span aria-hidden>&rarr;</span>}
                    </button>
                    <p className="text-[11px] text-aio-soft/80 max-w-xs">
                      By submitting you agree to be contacted by an A.I.O
                      sourcing specialist.
                    </p>
                  </div>
                </motion.form>
              ) : (
                <motion.div
                  key="confirmation"
                  data-testid="lead-confirmation"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="py-4"
                >
                  <div className="w-14 h-14 rounded-full bg-aio-olive text-aio-bg flex items-center justify-center mb-8">
                    <Check className="w-6 h-6" />
                  </div>
                  <p className="text-[11px] uppercase tracking-[0.32em] text-aio-soft mb-4">
                    Confirmation sent
                  </p>
                  <h3 className="font-display text-4xl sm:text-5xl leading-tight text-aio-ink mb-6">
                    Thank you, <span className="italic">{form.full_name.split(" ")[0] || "traveler"}</span>.
                  </h3>
                  <p className="text-aio-soft leading-relaxed text-base max-w-lg mb-10">
                    Check your email at <span className="text-aio-ink">{form.email}</span> for
                    further instructions. Your A.I.O sourcing specialist will
                    reach out within 24 hours. In the meantime, download your
                    private brochure below.
                  </p>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <button
                      type="button"
                      onClick={downloadPdf}
                      disabled={downloading}
                      data-testid="download-brochure-button"
                      className="inline-flex items-center gap-3 px-8 py-4 bg-aio-ink text-aio-bg hover:bg-black transition-colors text-sm uppercase tracking-[0.22em] disabled:opacity-50"
                    >
                      {downloading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      {downloading ? "Preparing PDF" : "Download Brochure (PDF)"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSubmitted(false);
                        setForm({ full_name: "", phone: "", email: "", locations: "", message: "" });
                      }}
                      data-testid="submit-another-button"
                      className="text-xs uppercase tracking-[0.28em] text-aio-soft hover:text-aio-ink"
                    >
                      Submit another request
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
